import React, { useEffect } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { NavigationContainer, LinkingOptions } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { Amplify } from 'aws-amplify';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import 'react-native-gesture-handler';
import amplifyConfig from './utils/amplify_config';
import './global.css';
import StoriesPage from './StoriesPage';
import StoryPage from './StoryPage';
import ExistingCharactersPage from './ExistingCharactersPage';
import CreateStoryPage from './CreateStoryPage';
import UpdateStoryPage from './UpdateStoryPage';
import AdminUserManagementPage from './AdminUserManagementPage';
import SettingsPage from './SettingsPage';
import SubscriptionPage from './SubscriptionPage';
import CharacterDetailsPage from './CharacterDetailsPage';
import NewCharacterPage from './NewCharacterPage';
import Tutorial from './tutorial';
import DeleteAccountPage from './DeleteAccountPage';
import ReferralPage from './ReferralPage';
import ParentalGateModal from './ParentalGateModal';
import { StoryProvider } from './StoryContext';
import { Story, StoryPageProps, CharacterDetailsPageProps } from './types';

Amplify.configure(amplifyConfig);

// SignUp form tweaks
const PDF_URL = "https://legal.interactivebedtime.com/terms.html";

const referralId = new URLSearchParams(window.location.search).get('referral') || '';

const formFields = {
  signUp: {
    email: { order: 1 },
    password: { order: 2 },
    confirm_password: { order: 3 },
    'custom:referrer_id': {
      order: 99,
      type: 'hidden',
      value: referralId,
    },
  },
};

const SignUpFooter = () => {
  const [showGate, setShowGate] = React.useState(false);
  const [pendingUrl, setPendingUrl] = React.useState<string | null>(null);

  const handleLinkClick = (url: string) => {
    setPendingUrl(url);
    setShowGate(true);
  };

  const handleGateSuccess = () => {
    if (pendingUrl) window.open(pendingUrl, '_blank');
    setShowGate(false);
    setPendingUrl(null);
  };

  const handleGateCancel = () => {
    setShowGate(false);
    setPendingUrl(null);
  };

  return (
    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
      By creating an account, you agree to our{' '}
      <a onClick={() => handleLinkClick(PDF_URL)} style={{ color: '#007aff', cursor: 'pointer' }}>
        Terms &amp; Conditions
      </a>{' '}
      and{' '}
      <a
        onClick={() => handleLinkClick('https://legal.interactivebedtime.com/privacy_policy.html')}
        style={{ color: '#007aff', cursor: 'pointer' }}
      >
        Privacy Policy
      </a>.
      {showGate && (
        <ParentalGateModal
          isVisible={showGate}
          onSuccess={handleGateSuccess}
          onCancel={handleGateCancel}
        />
      )}
    </div>
  );
};

const components = {
  SignUp: {
    Footer: SignUpFooter,
  },
};

export type RootStackParamList = {
  StoriesPage: undefined;
  ExistingCharactersPage: {
    selectedStory: Story;
    withImages: boolean;
    sessionId: string | undefined;
  };
  CreateStoryPage: undefined;
  UpdateStoryPage: { story: any };
  SettingsPage: undefined;
  SubscriptionPage: undefined;
  AdminUserManagementPage: undefined;
  StoryPage: StoryPageProps;
  CharacterDetailsPage: CharacterDetailsPageProps;
  NewCharacterPage: {
    selectedStory: Story | null;
    sessionId: string | null;
    withImages: boolean | null;
  };
  Tutorial: undefined;
  DeleteAccountPage: undefined;
  ReferralPage: undefined;
};

const linking: LinkingOptions<RootStackParamList> = {
  prefixes: [
    'http://localhost:19006',
    'https://dev.interactivebedtime.com',
    'https://test.interactivebedtime.com',
    'https://app.interactivebedtime.com',
    'https://main.d2uvngphib8ax9.amplifyapp.com',
  ],
  config: {
    screens: {
      StoriesPage: 'stories',
      StoryPage: 'story/:sessionId',
      ExistingCharactersPage: 'characters',
      CreateStoryPage: 'create-story',
      UpdateStoryPage: 'update-story/:storyId',
      SettingsPage: 'settings',
      SubscriptionPage: 'subscription',
      AdminUserManagementPage: 'admin',
      CharacterDetailsPage: 'character',
      NewCharacterPage: 'new-character',
      DeleteAccountPage: 'delete-account',
      ReferralPage: 'referrals'
    },
  },
};

const Stack = createStackNavigator<RootStackParamList>();

const AppNavigator = () => {
  const { authStatus } = useAuthenticator();

  return (
    <NavigationContainer linking={linking}>
      <Stack.Navigator initialRouteName={'StoriesPage'}>
        {authStatus === 'authenticated' && (
          <>
            <Stack.Screen name="StoriesPage" component={StoriesPage} options={{ headerShown: false }} />
            <Stack.Screen name="StoryPage" component={StoryPage} options={{ headerShown: false }} />
            <Stack.Screen name="ExistingCharactersPage" component={ExistingCharactersPage} options={{ headerShown: false }} />
            <Stack.Screen name="CreateStoryPage" component={CreateStoryPage} options={{ headerShown: false }} />
            <Stack.Screen name="UpdateStoryPage" component={UpdateStoryPage} options={{ headerShown: false }} />
            <Stack.Screen name="SettingsPage" component={SettingsPage} options={{ headerShown: false }} />
            <Stack.Screen name="SubscriptionPage" component={SubscriptionPage} options={{ headerShown: false }} />
            <Stack.Screen name="AdminUserManagementPage" component={AdminUserManagementPage} options={{ headerShown: false }} />
            <Stack.Screen name="CharacterDetailsPage" component={CharacterDetailsPage} options={{ headerShown: false }} />
            <Stack.Screen name="NewCharacterPage" component={NewCharacterPage} options={{ headerShown: false }} />
            <Stack.Screen name="Tutorial" component={Tutorial} options={{ headerShown: false }} />
            <Stack.Screen name="DeleteAccountPage" component={DeleteAccountPage} options={{ headerShown: false }} />
            <Stack.Screen name="ReferralPage" component={ReferralPage} options={{ headerShown: false }} />
          </>
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );
};

const App = () => {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : null;

  console.log('📍 Current pathname:', pathname);

  // ✅ Explicitly block rendering on landing page
  if (pathname === '/' || pathname === '/landing.html' || pathname === '/real-landing.html') {
    console.log('🛑 Skipping React app mount on landing page');
    return null;
  }

  return (
    <View style={styles.signInPage}>
      <StoryProvider>
        <Authenticator.Provider>
          <Authenticator formFields={formFields} components={components}>
            <AppNavigator />
          </Authenticator>
        </Authenticator.Provider>
      </StoryProvider>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F7F8FB',
  },
  signInPage: {
    flex: 1,
    justifyContent: 'center',
  },
  signOutButton: {
    alignSelf: 'flex-end',
    margin: 10,
  },
});

export default App;